<script lang="ts" setup>
import { ref } from "vue";
import AppButton from "@/components/atoms/button/AppButton.vue";

const visible = ref(false);
const isCorrect = ref(false);
const promiseResolver = ref<(() => void) | null>(null);

const show = (isValid: boolean) => {
  visible.value = true;
  return new Promise<void>(resolve => {
    promiseResolver.value = resolve;
    isCorrect.value = isValid;
  });
};

const onClose = () => {
  visible.value = false;
  if (promiseResolver.value) {
    promiseResolver.value();
    promiseResolver.value = null;
  }
};

defineExpose({
  show
});
</script>

<template>
  <BModal
    :visible="visible"
    hide-header
    hide-footer
    centered
    no-close-on-backdrop
    no-close-on-esc
    @hide="onClose"
  >
    <div class="modal-content-container">
      <div
        class="result-title"
        :class="{ correct: isCorrect, incorrect: !isCorrect }"
      >
        <template v-if="isCorrect">
          <span class="material-symbols-outlined circle">
            circle
          </span>
          正解
        </template>
        <template v-else>
          <span class="material-symbols-outlined cross">
            close
          </span>
          不正解
        </template>
      </div>

      <p class="message">
        {{ isCorrect ? "おめでとうございます！" : "もう一回答えてください" }}
      </p>

      <div class="mascot-container">
        <img
          v-if="isCorrect"
          src="@/assets/img/caikuma_jumping.svg?webp"
          alt="正解"
          class="mascot-image"
        />
        <img
          v-else
          src="@/assets/img/caikuma_confused.svg?webp"
          alt="不正解"
          class="mascot-image"
        />
      </div>

      <AppButton block @click="onClose" class="ok-button">
        OK
      </AppButton>
    </div>
  </BModal>
</template>

<style lang="scss" scoped>
$color-correct: #f4a722;
$color-incorrect: #c2493e;

.modal-content {
  border-radius: 8px;
}

.modal-content-container {
  padding: calc(40px - 1rem) calc(24px - 1rem) calc(24px - 1rem); // modalのpaddingを打ち消す
  text-align: center;
}

.message {
  margin-bottom: 1.5rem;
  font-size: 16px;
}

.result-title {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 40px;
  font-weight: 600;

  &.correct {
    color: $color-correct;
  }

  &.incorrect {
    color: $color-incorrect;
  }
}

.circle {
  font-size: 50px;
  font-weight: 600;
  color: $color-correct;
}

.cross {
  font-size: 54px;
  font-weight: 600;
  color: $color-incorrect;
}

.mascot-container {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}

.mascot-image {
  max-width: 150px;
  height: auto;
}
</style>
